import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import DescriptionIcon from '@mui/icons-material/Description'
import { useSnackbar } from 'notistack'
import { onboardingService } from '@/services/onboardingService'
import { documentoService } from '@/services/documentoService'
import { Onboarding } from '@/types/onboarding'

/**
 * Primeiro acesso do consultor (PDF do Consultor, Etapa 4).
 *
 * <p>Concluir aqui é a "liberação para criação de reservas" — mas só para quem a
 * Originei já aprovou. O backend recusa a promoção sem aprovação; a tela deixa isso
 * visível em vez de mostrar um botão que vai falhar.
 */
const PrimeiroAcessoPage: FC = () => {
  /**
   * Baixa o anexo pelo backend. A URL pré-assinada do MinIO aponta para o host interno,
   * inalcançável pelo browser — mesmo motivo da tela de documentos.
   */
  const baixarMaterial = async (documentoId: string) => {
    const blob = await documentoService.baixarArquivo(documentoId)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'material-originei'
    link.click()
    URL.revokeObjectURL(url)
  }

  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [estado, setEstado] = useState<Onboarding | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [ativa, setAtiva] = useState(0)
  const [concluindo, setConcluindo] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const dados = await onboardingService.obter()
      setEstado(dados)
      // Abre na primeira etapa ainda não vista, não sempre no começo.
      const primeiraPendente = dados.etapas.findIndex((e) => !e.concluida)
      setAtiva(primeiraPendente === -1 ? dados.etapas.length - 1 : primeiraPendente)
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Falha ao carregar o primeiro acesso')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    carregar()
  }, [carregar])

  const avancar = async (indice: number) => {
    if (!estado) return
    const etapa = estado.etapas[indice]
    try {
      const atualizado = await onboardingService.concluirEtapa(etapa.etapa)
      setEstado(atualizado)
      setAtiva(Math.min(indice + 1, atualizado.etapas.length - 1))
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao registrar a etapa',
        { variant: 'error' }
      )
    }
  }

  const concluir = async () => {
    setConcluindo(true)
    try {
      await onboardingService.concluir()
      enqueueSnackbar(
        'Acesso liberado. Entre novamente para começar a criar reservas.',
        { variant: 'success', autoHideDuration: 8000 }
      )
      // O papel novo só existe no PRÓXIMO token: sem novo login, o consultor
      // continuaria esbarrando em 403 sem entender por quê.
      navigate('/login')
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Não foi possível concluir agora',
        { variant: 'error' }
      )
    } finally {
      setConcluindo(false)
    }
  }

  if (carregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (erro) {
    return <Alert severity="error" sx={{ m: 3 }}>{erro}</Alert>
  }

  if (!estado) return null

  if (estado.liberado) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="success"
          action={
            <Button size="small" onClick={() => navigate('/dashboard')}>
              Ir para o dashboard
            </Button>
          }
        >
          Seu acesso já está liberado.
        </Alert>
      </Box>
    )
  }

  const vistas = estado.etapas.filter((e) => e.concluida).length
  const progresso = estado.etapas.length ? (vistas / estado.etapas.length) * 100 : 0

  return (
    <Box sx={{ p: 3, maxWidth: 820, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Bem-vindo à Originei
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Antes de criar sua primeira reserva, conheça como a plataforma funciona.
      </Typography>

      {estado.pendencia && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Pendência no seu cadastro</Typography>
          <Typography variant="body2">{estado.pendencia}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Entre em contato com a Originei para regularizar. Enquanto houver pendência, o
            acesso não é liberado.
          </Typography>
        </Alert>
      )}

      {estado.aguardandoAprovacao && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Seu cadastro está em validação pela Originei. Você já pode percorrer o conteúdo;
          a liberação para criar reservas acontece assim que a validação terminar.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">Progresso</Typography>
            <Typography variant="caption" color="text.secondary">
              {vistas} de {estado.etapas.length}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progresso} />
        </CardContent>
      </Card>

      <Stepper activeStep={ativa} orientation="vertical" nonLinear>
        {estado.etapas.map((etapa, i) => (
          <Step key={etapa.etapa} completed={etapa.concluida}>
            <StepLabel onClick={() => setAtiva(i)} sx={{ cursor: 'pointer' }}>
              {etapa.titulo}
            </StepLabel>
            <StepContent>
              <Stack spacing={2}>
                {etapa.conteudo ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {etapa.conteudo}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Conteúdo em preparação.
                  </Typography>
                )}

                {etapa.videoUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayCircleOutlineIcon />}
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    href={etapa.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Assistir
                  </Button>
                )}

                {/*
                  Anexo da etapa: o material institucional costuma chegar como PDF, e não
                  como texto digitado — foi o que o time comercial pediu ("o local para
                  incluir o arquivo").
                */}
                {etapa.documentoId && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionIcon />}
                    onClick={() => void baixarMaterial(etapa.documentoId as string)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Baixar o material
                  </Button>
                )}

                {etapa.etapa === 'PRODUTOS' && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/produtos')}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Abrir a biblioteca de produtos
                  </Button>
                )}

                <Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => avancar(i)}
                    disabled={etapa.concluida && i === estado.etapas.length - 1}
                  >
                    {etapa.concluida ? 'Avançar' : 'Marcar como lido'}
                  </Button>
                </Box>
              </Stack>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      <Divider sx={{ my: 3 }} />

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={concluir}
        disabled={!estado.podeConcluir || concluindo}
      >
        {concluindo ? 'Liberando…' : 'Concluir e liberar meu acesso'}
      </Button>
      {!estado.podeConcluir && !estado.pendencia && !estado.aguardandoAprovacao && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Marque todas as etapas como lidas para liberar o acesso.
        </Typography>
      )}
    </Box>
  )
}

export default PrimeiroAcessoPage
